import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, Users, Settings, ClipboardList, Trophy, Target } from 'lucide-react';
import { useTeamStore } from '../../store/teamStore';
import WhistleIcon from '../icons/WhistleIcon';
import { hasGames, getEventLabel } from '../../data/sportRulesets';

const MobileNav: React.FC = () => {
  const location = useLocation();
  const { currentTeam } = useTeamStore();

  // Roster and Lineup manage a multi-player roster, which a personal
  // (parent/player) account's one-player team has no use for. Practices
  // stays available — generating a practice from one athlete's own stats is
  // a core selling point, not a coaching-only feature.
  const isPersonalAccount = Boolean(currentTeam?.isPersonal);
  // GamePlanManagement.tsx hard-blocks every sport except Football with an
  // "only available for football teams" message — only show the link where
  // it actually leads somewhere.
  const showGamePlans = isPersonalAccount && currentTeam?.sport === 'Football';

  const navigation = [
    { name: 'Home', href: '/', icon: Home },
    ...(isPersonalAccount ? [] : [{ name: 'Roster', href: '/players', icon: Users }]),
    ...(isPersonalAccount ? [] : [{ name: 'Lineup', href: '/depth-chart', icon: ClipboardList }]),
    { name: 'Practices', href: '/practices', icon: WhistleIcon },
    // { name: 'Playbook', href: '/playbook', icon: BookOpen },
    // Game Plans is otherwise only reachable via the TeamHub tile, which a
    // personal account never sees (no team-switcher to click into TeamHub from).
    ...(showGamePlans ? [{ name: 'Game Plans', href: '/game-plans', icon: Target }] : []),
    // Show Games/Matches for all sports that have event tracking
    ...(hasGames(currentTeam?.sport)
      ? [{ name: getEventLabel(currentTeam?.sport).plural, href: '/games', icon: Trophy }]
      : []),
    { name: 'Settings', href: '/settings', icon: Settings },
  ];

  const handlePracticesClick = (e: React.MouseEvent, href: string) => {
    if (href === '/practices' && location.pathname === '/practices') {
      e.preventDefault();
      // Trigger a custom event that PracticeManagement will listen for
      window.dispatchEvent(new CustomEvent('backToPractices'));
    }
  };

  return (
    <nav className="bg-white border-t border-gray-200 shadow-lg">
      <div className="flex justify-around items-center px-1 py-2">
        {navigation.map((item) => {
          const isActive = location.pathname === item.href || 
                          (item.href !== '/' && location.pathname.startsWith(item.href));
          return (
            <Link
              key={item.name}
              to={item.href}
              onClick={(e) => handlePracticesClick(e, item.href)}
              className="flex flex-col items-center justify-center px-1 py-1 min-w-0 flex-1"
              style={{ minHeight: '56px' }}
            >
              <div 
                className={`flex items-center justify-center rounded-full w-8 h-8 mb-1 transition-colors ${
                  isActive ? 'bg-primary bg-opacity-10 text-primary' : 'text-gray-600'
                }`}
              >
                <item.icon className={`h-5 w-5 ${isActive ? 'text-primary' : 'text-gray-600'}`} />
              </div>
              <span 
                className={`text-xs leading-tight text-center ${
                  isActive ? 'text-primary font-medium' : 'text-gray-600'
                }`}
                style={{ fontSize: '10px', lineHeight: '12px' }}
              >
                {item.name}
              </span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
};

export default MobileNav; 